import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Loading } from "@/components/Loading";
import { Link, useNavigate, useParams } from "react-router-dom";
import { backOrigin, useBackTo } from "@/hooks/useBackTo";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { ApiError } from "@/lib/apiClient";
import { toastApiError } from "@/lib/toast";
import { FIELD_MAX, tooLong } from "@/lib/fieldLimits";
import { products as productsApi, salesCampaigns as campaignsApi, PRICE_LEVELS, SALES_CAMPAIGN_STATUSES, type ProductListItem, type SalesCampaignDetail } from "@/features/catalog/api/catalog.api";
import { toApiDateTime, dateOnly } from "@/utils/dates";
import { users as usersApi, type UserListItem } from "@/features/users/api/users.api";
import { USER_ROLE_TIER_LABELS } from "@/features/auth/api/auth.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Form } from "@/components/ui/form";
import { TextField, TextAreaField, SelectField, DateField, useAutoFocus, useUnsavedChangesWarning } from "@/components/form";
import { Notice } from "@/components/Notice";
import { SearchField } from "@/components/SearchInput";
import { Dash } from "@/components/Dash";
import { useConfirm } from "@/components/ConfirmDialog";

const schema = z.object({
  name: z.string().trim().min(1, "Campaign name is required.").max(FIELD_MAX.salesCampaign.name, tooLong("Name", FIELD_MAX.salesCampaign.name)),
  priceLevel: z.string(),
  status: z.string(),
  validFrom: z.string(),
  validTo: z.string(),
  description: z.string().max(FIELD_MAX.salesCampaign.description, tooLong("Description", FIELD_MAX.salesCampaign.description)),
});

type FormValues = z.infer<typeof schema>;


function toForm(c: SalesCampaignDetail): FormValues {
  return {
    name: c.name,
    priceLevel: String(c.priceLevel),
    status: c.status,
    validFrom: dateOnly(c.validFrom),
    validTo: dateOnly(c.validTo),
    description: c.description ?? "",
  };
}

export default function SalesCampaignEditPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const backTo = useBackTo("/manage/sales-campaigns");
  const isEdit = id !== undefined;
  const confirm = useConfirm();

  const [base, setBase] = useState<SalesCampaignDetail>(blankCampaign());
  const [productIds, setProductIds] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [allProducts, setAllProducts] = useState<ProductListItem[] | null>(null);
  const [allUsers, setAllUsers] = useState<UserListItem[] | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: toForm(blankCampaign()),
  });

  useAutoFocus(form, "name", !isEdit);

  useEffect(() => {
    let cancelled = false;
    productsApi.listAll()
      .then((p) => { if (!cancelled) setAllProducts(p); })
      .catch(() => { if (!cancelled) setAllProducts([]); });
    usersApi.listAll()
      .then((u) => { if (!cancelled) setAllUsers(u); })
      .catch(() => { if (!cancelled) setAllUsers([]); });
    if (isEdit && id) {
      campaignsApi.get(id)
        .then((c) => {
          if (cancelled) return;
          setBase(c);
          setProductIds(c.productIds);
          setUserIds(c.userIds);
          form.reset(toForm(c));
          setLoading(false);
        })
        .catch((e) => {
          if (cancelled) return;
          if (e instanceof ApiError && e.status === 404) setNotFound(true);
          else setError(e instanceof Error ? e.message : "Failed to load campaign.");
          setLoading(false);
        });
    }
    return () => { cancelled = true; };
  }, [id, isEdit, form]);

  function toggleProduct(pid: string) {
    setProductIds((prev) => prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]);
  }
  function toggleUser(uid: string) {
    setUserIds((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
  }

  function toggleAll(check: boolean, ids: string[], setIds: (fn: (prev: string[]) => string[]) => void) {
    setIds((prev) => {
      if (check) return Array.from(new Set([...prev, ...ids]));
      const rm = new Set(ids);
      return prev.filter((x) => !rm.has(x));
    });
  }

  const headerState = (ids: string[], selected: Set<string>): boolean | "indeterminate" => {
    if (ids.length === 0) return false;
    const n = ids.reduce((acc, id) => acc + (selected.has(id) ? 1 : 0), 0);
    return n === 0 ? false : n === ids.length ? true : "indeterminate";
  };

  const priceLevel = Number(form.watch("priceLevel"));

  const filteredProducts = useMemo(() => {
    if (!allProducts) return [];
    const q = productSearch.trim().toLowerCase();
    if (!q) return allProducts;
    return allProducts.filter((p) =>
      p.name.toLowerCase().includes(q) ||
      (p.sku ?? "").toLowerCase().includes(q) ||
      (p.manufacturer ?? "").toLowerCase().includes(q),
    );
  }, [allProducts, productSearch]);

  const filteredUsers = useMemo(() => {
    if (!allUsers) return [];
    const q = userSearch.trim().toLowerCase();
    if (!q) return allUsers;
    return allUsers.filter((u) =>
      u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    );
  }, [allUsers, userSearch]);

  const selectedSet = useMemo(() => new Set(productIds), [productIds]);
  const selectedUsersSet = useMemo(() => new Set(userIds), [userIds]);

  function buildDto(v: FormValues): SalesCampaignDetail {
    return {
      ...base,
      name: v.name.trim(),
      description: v.description.trim() || null,
      priceLevel: Number(v.priceLevel),
      status: v.status,
      validFrom: toApiDateTime(v.validFrom),
      validTo: toApiDateTime(v.validTo),
      productIds,
      userIds,
    };
  }

  async function onValid(v: FormValues) {
    if (isEdit && !form.formState.isDirty) {
      toast.info("No changes to save");
      return;
    }
    setError(null);
    try {
      const dto = buildDto(v);
      if (isEdit && id) {
        await campaignsApi.update(id, dto);
        setBase(dto);
        setProductIds(dto.productIds);
        setUserIds(dto.userIds);
        form.reset(toForm(dto));
        toast.success("Sales campaign updated");
      } else {
        const created = await campaignsApi.create(dto);
        toast.success("Sales campaign created");
        navigate(`/manage/sales-campaigns/${created.id}`, { replace: true, state: backOrigin(backTo) });
      }
    } catch (e) {
      toastApiError(e, "Save failed.");
    }
  }

  function onInvalid() {
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>('[aria-invalid="true"]');
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.focus({ preventScroll: true });
    });
  }

  async function onDelete() {
    if (!id) return;
    if (!(await confirm({ description: "Delete this campaign? Quotes already created from it are unaffected.", confirmLabel: "Delete", destructive: true }))) return;
    try {
      await campaignsApi.remove(id);
      navigate(backTo, { replace: true });
    } catch (e) {
      toastApiError(e, "Delete failed.");
    }
  }

  const saving = form.formState.isSubmitting;
  useUnsavedChangesWarning(form.formState.isDirty);

  return (
    <>
      <div className="mb-4">
        <Link to={backTo} className="text-sm text-muted-foreground hover:underline">
          ← Back to sales campaigns
        </Link>
      </div>

      <header className="mb-6 flex items-start justify-between gap-4">
        <h1 className="text-2xl font-semibold text-slate-800">
          {isEdit ? "Edit sales campaign" : "New sales campaign"}
        </h1>
        {isEdit && (
          <Button type="button" variant="destructive" onClick={onDelete}>
            Delete campaign
          </Button>
        )}
      </header>

      {notFound ? (
        <Notice tone="info">Campaign not found.</Notice>
      ) : loading ? (
        <Loading>Loading campaign…</Loading>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onValid, onInvalid)} className="space-y-6">
            <Panel title="Details">
              <Grid>
                <TextField control={form.control} name="name" label="Name" required />
                <SelectField
                  control={form.control}
                  name="priceLevel"
                  label="Price level"
                  options={PRICE_LEVELS.map((n) => [String(n), `Level ${n}${n === 1 ? " (Retail)" : ""}`])}
                />
                <SelectField
                  control={form.control}
                  name="status"
                  label="Status"
                  options={SALES_CAMPAIGN_STATUSES.map((s) => [s, s])}
                />
                <DateField control={form.control} name="validFrom" label="Valid from" />
                <DateField control={form.control} name="validTo" label="Valid to" />
              </Grid>
              <div className="mt-4">
                <TextAreaField control={form.control} name="description" label="Description" rows={2} />
              </div>
            </Panel>

            <Panel title={`Products (${productIds.length} selected)`}>
              {allProducts === null ? (
                <Loading>Loading products…</Loading>
              ) : allProducts.length === 0 ? (
                <Notice tone="info">
                  No products exist yet. Create some in Manage → Products first,
                  then come back to add them to this campaign.
                </Notice>
              ) : (
                <>
                  <SearchField
                    placeholder="Search by name, SKU, or manufacturer…"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    wrapperClassName="mb-3"
                  />
                  <Table containerClassName="max-h-96 border border-slate-200 rounded">
                    <TableHeader className="bg-slate-50 sticky top-0 z-10">
                        <TableRow>
                          <TableHead className="w-10 [&>[role=checkbox]]:translate-y-0">
                            <Checkbox
                              checked={headerState(filteredProducts.map((p) => p.id), selectedSet)}
                              onCheckedChange={(c) => toggleAll(c === true, filteredProducts.map((p) => p.id), setProductIds)}
                              aria-label="Select all products"
                            />
                          </TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>SKU</TableHead>
                          <TableHead className="text-right">L{priceLevel}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredProducts.map((p) => {
                          const checked = selectedSet.has(p.id);
                          return (
                            <TableRow
                              key={p.id}
                              className={`cursor-pointer ${checked ? "bg-emerald-50/40" : ""}`}
                              onClick={() => toggleProduct(p.id)}
                            >
                              <TableCell onClick={(e) => e.stopPropagation()} className="[&>[role=checkbox]]:translate-y-0">
                                <Checkbox checked={checked} onCheckedChange={() => toggleProduct(p.id)} />
                              </TableCell>
                              <TableCell className="font-medium text-slate-900">{p.name}</TableCell>
                              <TableCell className="text-slate-600">{p.sku ?? <Dash />}</TableCell>
                              <TableCell className="text-right tabular-nums text-slate-700">${p.priceLevel1.toFixed(2)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  <p className="text-xs text-muted-foreground mt-2">
                    Tip: the price tier shown here is L1 from the list payload;
                    the actual quoted price uses the campaign&apos;s Level {priceLevel}.
                  </p>
                </>
              )}
            </Panel>

            <Panel title={`Authorized users (${userIds.length === 0 ? "open to all" : `${userIds.length} restricted`})`}>
              {allUsers === null ? (
                <Loading>Loading users…</Loading>
              ) : allUsers.length === 0 ? (
                <Notice tone="info">No portal users to grant access to.</Notice>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mb-2">
                    Leave empty to make this campaign available to every user.
                    Check users to restrict it to just them — admins always
                    retain access regardless.
                  </p>
                  <SearchField
                    placeholder="Search users by name or email…"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    wrapperClassName="mb-3"
                  />
                  <Table containerClassName="max-h-64 border border-slate-200 rounded">
                      <TableHeader className="bg-slate-50 sticky top-0 z-10">
                        <TableRow>
                          <TableHead className="w-10 [&>[role=checkbox]]:translate-y-0">
                            <Checkbox
                              checked={headerState(filteredUsers.map((u) => u.id), selectedUsersSet)}
                              onCheckedChange={(c) => toggleAll(c === true, filteredUsers.map((u) => u.id), setUserIds)}
                              aria-label="Select all users"
                            />
                          </TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Role</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredUsers.map((u) => {
                          const checked = selectedUsersSet.has(u.id);
                          return (
                            <TableRow
                              key={u.id}
                              className={`cursor-pointer ${checked ? "bg-emerald-50/40" : ""}`}
                              onClick={() => toggleUser(u.id)}
                            >
                              <TableCell onClick={(e) => e.stopPropagation()} className="[&>[role=checkbox]]:translate-y-0">
                                <Checkbox checked={checked} onCheckedChange={() => toggleUser(u.id)} />
                              </TableCell>
                              <TableCell className="font-medium text-slate-900">{u.name}</TableCell>
                              <TableCell className="text-slate-700">{u.email}</TableCell>
                              <TableCell className="text-slate-600 text-xs">{USER_ROLE_TIER_LABELS[u.role]}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                </>
              )}
            </Panel>

            {error && <Notice tone="error">{error}</Notice>}

            <div className="flex items-center justify-end gap-3">
              <Button asChild variant="ghost">
                <Link to={backTo}>Cancel</Link>
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Saving…" : isEdit ? "Save campaign" : "Create campaign"}
              </Button>
            </div>
          </form>
        </Form>
      )}
    </>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader className="border-b py-3">
        <CardTitle className="text-base font-semibold text-slate-900">{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-6">{children}</CardContent>
    </Card>
  );
}

function Grid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>;
}

function blankCampaign(): SalesCampaignDetail {
  return {
    id: "", name: "", description: null,
    priceLevel: 1, status: "Active",
    validFrom: null, validTo: null,
    productIds: [],
    userIds: [],
    createdAt: "", updatedAt: "",
  };
}
